/**
 * Webhooks — machine-to-machine EVENT distribution (its own page, deliberately
 * separate from Notifications, which delivers rendered alerts to humans). An
 * endpoint is a URL + a signing secret (shown once at create/rotate);
 * subscriptions name the EVENT types it receives; Send test fires a signed
 * envelope so the receiver can verify its setup.
 */
import { useState } from 'react';
import {
  Banner,
  Button,
  Card,
  Input,
  Modal,
  Skeleton,
  Table,
} from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Toggle } from '../../components/Toggle.js';
import { useMe } from '../../hooks/useMe.js';
import type { Webhook, WebhookWithSecret } from '../../lib/types.js';
import {
  isValidWebhookLabel,
  useCreateWebhookLogic,
  useWebhookActionsLogic,
  useWebhooksListLogic,
  slugifyWebhookLabel,
} from './useWebhooksLogic.js';

/** Shown exactly once — the platform can never display it again. Open state
 * is prop-driven (house rule: never conditionally mount a Modal). */
const SecretReveal = ({ secret, onDone }: { secret: string | null; onDone: () => void }) => (
  <Modal open={secret !== null} onOpenChange={onDone}>
    <Modal.Header>
      <Modal.Title>Signing secret — copy it now</Modal.Title>
      <Modal.CloseButton />
    </Modal.Header>
    <Modal.Body>
      <p className="text-ink-muted mb-3 text-sm">
        Paste this into the receiving system. It is shown once and cannot be retrieved again —
        rotate to get a new one.
      </p>
      <pre className="overflow-x-auto rounded-lg border border-outline bg-surface p-3 text-xs text-ink">
        {secret ?? ''}
      </pre>
    </Modal.Body>
    <Modal.Footer>
      <Button onClick={onDone}>Done</Button>
    </Modal.Footer>
  </Modal>
);

const AddWebhookDialog = ({
  tenantId,
  open,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  open: boolean;
  onSuccess: (webhook: WebhookWithSecret) => void;
  onCancel: () => void;
}) => {
  const logic = useCreateWebhookLogic(tenantId, onSuccess);
  const labelValid = isValidWebhookLabel(logic.label);
  const urlValid = logic.url.startsWith('https://');
  const canSubmit = !logic.isSubmitting && labelValid && urlValid;
  return (
    <Modal open={open} onOpenChange={onCancel}>
      <Modal.Header>
        <Modal.Title>Add webhook</Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body>
        {logic.error && (
          <Banner variant="danger" className="mb-3">
            <Banner.Body>
              <Banner.Title>{logic.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}
        <label htmlFor="wh-label" className="text-ink mb-1.5 block text-xs font-semibold">
          Name
        </label>
        <Input
          id="wh-label"
          value={logic.label}
          onChange={(e) => logic.setLabel(slugifyWebhookLabel(e.target.value))}
          placeholder="e.g., ricky-curator"
          autoComplete="off"
          error={logic.label !== '' && !labelValid}
        />
        <p className="text-ink-subtle mt-1.5 text-xs">
          Lowercase letters, numbers and hyphens — at least 3 characters. Typing does the
          lowercasing for you.
        </p>
        <label htmlFor="wh-url" className="text-ink mb-1.5 mt-3.5 block text-xs font-semibold">
          Receiver URL
        </label>
        <Input
          id="wh-url"
          value={logic.url}
          onChange={(e) => logic.setUrl(e.target.value)}
          placeholder="https://…"
          autoComplete="off"
        />
        <p className="text-ink-subtle mt-1.5 text-xs">
          Must be https. Deliveries are HMAC-signed; the secret appears once after creation.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        {!canSubmit && !logic.isSubmitting && (logic.label !== '' || logic.url !== '') && (
          <span className="text-ink-subtle mr-auto self-center text-xs">
            {!labelValid
              ? 'The name needs at least 3 characters.'
              : 'The URL must start with https://.'}
          </span>
        )}
        <Button disabled={!canSubmit} onClick={() => void logic.submit()}>
          {logic.isSubmitting ? 'Adding…' : 'Add webhook'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** One webhook as a table row — same rhythm as the Destinations table. Test
 * results/errors render as a small line under the name; the row stays one
 * line tall until it has something to say. */
const WebhookRow = ({ tenantId, webhook }: { tenantId: string; webhook: Webhook }) => {
  const actions = useWebhookActionsLogic(tenantId);
  const [secret, setSecret] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
    at: string;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const rotate = async () => {
    const res = await actions.rotateSecret(webhook.id);
    setSecret(res.secret);
  };
  const sendTest = async () => {
    setTestResult(null);
    const at = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    try {
      const res = await actions.sendTest(webhook.id);
      setTestResult({ ok: true, message: `receiver answered ${String(res.status)}`, at });
    } catch (err) {
      setTestResult({
        ok: false,
        message: err instanceof Error ? err.message : 'delivery failed',
        at,
      });
    }
  };

  const statusLine = actions.error ? (
    <p className="text-danger mt-1 text-xs font-semibold">{actions.error.message}</p>
  ) : testResult ? (
    <p className={`mt-1 text-xs font-semibold ${testResult.ok ? 'text-success' : 'text-danger'}`}>
      {testResult.ok
        ? `✓ Test delivered ${testResult.at} — ${testResult.message}`
        : `⚠ Test failed ${testResult.at} — ${testResult.message}`}
    </p>
  ) : null;

  return (
    <Table.Row>
      <Table.Cell>
        <p className="text-ink text-sm font-bold">{webhook.label}</p>
        {statusLine}
      </Table.Cell>
      <Table.Cell>
        <span className="text-ink-muted break-all text-xs">{webhook.url}</span>
      </Table.Cell>
      <Table.Cell>
        <Toggle
          checked={webhook.isEnabled}
          onChange={() => void actions.setEnabled(webhook.id, !webhook.isEnabled)}
          label={`Enable ${webhook.label}`}
        />
      </Table.Cell>
      <Table.Cell align="right">
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => void sendTest()}>
            Send test
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void rotate()}>
            Rotate secret
          </Button>
          {confirmDelete ? (
            <Button variant="danger" size="sm" onClick={() => void actions.remove(webhook.id)}>
              Really delete?
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
        <SecretReveal secret={secret} onDone={() => setSecret(null)} />
      </Table.Cell>
    </Table.Row>
  );
};

export const WebhooksPage = () => {
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;
  const listQuery = useWebhooksListLogic(tenantId);
  const [showAdd, setShowAdd] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);

  if (meQuery.isLoading || !tenantId || listQuery.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-2 h-7 w-48" />
        <Skeleton className="mb-6 h-4 w-96" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </AppShell>
    );
  }

  const webhooks = listQuery.data ?? [];

  return (
    <AppShell>
      <h2 className="text-ink text-2xl font-bold">Webhooks</h2>
      <p className="text-ink-muted mb-5 mt-1.5 max-w-[72ch] text-sm">
        Systems the platform can deliver to. Workflows decide what gets sent where — this page only
        manages the connections: each delivery is HMAC-signed, and the receiving system verifies it
        with the shared secret.
      </p>

      {listQuery.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{listQuery.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      <div className="mb-3.5 flex justify-end">
        <Button onClick={() => setShowAdd(true)}>+ Add webhook</Button>
      </div>
      {webhooks.length === 0 ? (
        <Card padding="md">
          <Card.Title>No webhooks yet</Card.Title>
          <p className="text-ink-muted mt-2 text-sm">
            Add an endpoint to start distributing events — for example, Ricky Curator consuming
            <code className="mx-1">feedback.kb_verdict</code> so knowledge-base fixes land in the
            curator’s queue.
          </p>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-outline">
          <Table.Root className="w-full min-w-[48rem] text-sm">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Webhook</Table.HeaderCell>
                <Table.HeaderCell>Receiver</Table.HeaderCell>
                <Table.HeaderCell>Enabled</Table.HeaderCell>
                <Table.HeaderCell align="right">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {webhooks.map((webhook) => (
                <WebhookRow key={webhook.id} tenantId={tenantId} webhook={webhook} />
              ))}
            </Table.Body>
          </Table.Root>
        </div>
      )}

      <AddWebhookDialog
        tenantId={tenantId}
        open={showAdd}
        onCancel={() => setShowAdd(false)}
        onSuccess={(webhook) => {
          setShowAdd(false);
          setCreatedSecret(webhook.secret);
        }}
      />
      <SecretReveal secret={createdSecret} onDone={() => setCreatedSecret(null)} />
    </AppShell>
  );
};
